"use client";

import { MARKETING_ORIGIN } from "@klarify/constants";
import { Link } from "@klarify/i18n/navigation";
import { Trans, useLingui } from "@lingui/react/macro";
import { Menu } from "lucide-react";
import KlarifyLogo from "../svg/klarify-logo";
import { Button } from "../ui/button";
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetTitle,
	SheetTrigger,
} from "../ui/sheet";

const NAV_LINKS: {
	labelKey: "findTherapist" | "blogs" | "clinics";
	href: string;
}[] = [
	{ labelKey: "findTherapist", href: "/therapists" },
	{ labelKey: "blogs", href: "/blogs" },
	{ labelKey: "clinics", href: "/clinics" },
];

export const PublicHeader = () => {
	const { t } = useLingui();
	const translatePublicProfilePublicHeader = (key: string) => {
		const messages: Record<string, string> = {
			home: t`Klarify home`,
			therapistPrompt: t`Therapist?`,
			tryKlarify: t`Try Klarify`,
			openMenu: t`Open menu`,
			navigation: t`Navigation`,
			"nav.findTherapist": t`Find a therapist`,
			"nav.blogs": t`Blogs`,
			"nav.clinics": t`Clinics`,
		};
		return messages[key] ?? key;
	};

	return (
		<header className="sticky top-0 z-40 px-4 pt-6 sm:px-6">
			<div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 rounded-lg border border-white/40 bg-white/40 px-4 py-2 shadow-[0_1px_3px_rgba(0,0,0,0.04)] backdrop-blur-sm sm:px-6">
				<Link
					href={MARKETING_ORIGIN}
					className="flex shrink-0 items-center"
					aria-label={t`Klarify home`}
				>
					<KlarifyLogo
						type="text"
						width={110}
						height={32}
					/>
				</Link>
				<nav className="hidden items-center gap-1 md:flex">
					{NAV_LINKS.map((link) => (
						<Link
							key={link.labelKey}
							href={link.href}
							className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 font-medium text-klarify-neutral-700 text-sm transition-colors hover:bg-klarify-neutral-100"
						>
							{translatePublicProfilePublicHeader(`nav.${link.labelKey}`)}
						</Link>
					))}
				</nav>
				<div className="flex items-center gap-2 sm:gap-3">
					<span className="hidden font-medium text-klarify-neutral-700 text-sm sm:inline">
						<Trans>Therapist?</Trans>
					</span>
					<Link
						href={MARKETING_ORIGIN}
						className="inline-flex items-center justify-center rounded-sm bg-klarify-forest-950 px-4 py-2 font-semibold text-sm text-white transition-colors hover:bg-klarify-forest-900"
					>
						<Trans>Try Klarify</Trans>
					</Link>
					<Sheet>
						<SheetTrigger asChild>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								rounding="default"
								aria-label={t`Open menu`}
								className="md:hidden"
							>
								<Menu className="h-5 w-5 text-klarify-neutral-700" />
							</Button>
						</SheetTrigger>
						<SheetContent
							side="right"
							className="w-72"
						>
							<SheetTitle className="sr-only">
								<Trans>Navigation</Trans>
							</SheetTitle>
							<nav className="mt-8 flex flex-col gap-1">
								{NAV_LINKS.map((link) => (
									<SheetClose
										key={link.labelKey}
										asChild
									>
										<Link
											href={link.href}
											className="rounded-md px-3 py-2 font-medium text-base text-klarify-neutral-800 transition-colors hover:bg-klarify-neutral-100"
										>
											{translatePublicProfilePublicHeader(
												`nav.${link.labelKey}`,
											)}
										</Link>
									</SheetClose>
								))}
							</nav>
						</SheetContent>
					</Sheet>
				</div>
			</div>
		</header>
	);
};
